import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ElementRef,
} from '@angular/core';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import {
  Subject,
  takeUntil,
  debounceTime,
  distinctUntilChanged,
  switchMap,
} from 'rxjs';
import { FormlyFieldProps } from '@ngx-formly/core';

interface ServiceOption {
  label: string;
  value: string;
}

interface ExtendedFieldProps extends FormlyFieldProps {
  onSearch?: (searchTerm: string) => void;
  onLoadMore?: () => void;
  hasMoreData?: () => boolean;
  isLoading?: () => boolean;
  isLoadingMore?: () => boolean;
  searchPlaceholder?: string;
  noResultsText?: string;
  loadingText?: string;
  loadingMoreText?: string;
  options?: ServiceOption[];
  excludeIds?: string[];
}

@Component({
  selector: 'irembogov-custom-service-selector',
  templateUrl: './custom-service-selector.component.html',
  styleUrls: ['./custom-service-selector.component.scss'],
})
export class CustomServiceSelectorComponent
  extends FieldType<FieldTypeConfig>
  implements OnInit, OnDestroy
{
  private destroy$ = new Subject<void>();

  items: ServiceOption[] = [];
  searchTerm$ = new Subject<string>();

  ngOnInit(): void {
    this.initializeOptions();
    this.setupSearch();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeOptions(): void {
    const options = (this.props as ExtendedFieldProps).options || [];
    this.items = this.filterOptions([...options]);

    if (this.field.formControl) {
      this.field.options?.fieldChanges
        ?.pipe(
          debounceTime(50), // Small debounce to batch rapid updates
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          const newOptions = (this.props as ExtendedFieldProps).options || [];
          const filteredOptions = this.filterOptions([...newOptions]);

          // Only update if options actually changed
          if (JSON.stringify(filteredOptions) !== JSON.stringify(this.items)) {
            this.items = filteredOptions;
            this.cdr.detectChanges();
          }
        });
    }

    // Reactive option checking instead of polling
    if (this.field.props) {
      // Check for props changes reactively
      const propsChangeSubscription = new Subject<void>();

      // Set up a property observer for options
      const originalOptions = this.field.props.options;
      let lastOptionsString = JSON.stringify(originalOptions);

      const checkOptionsChange = () => {
        const currentOptions = (this.props as ExtendedFieldProps).options || [];
        const currentOptionsString = JSON.stringify(currentOptions);

        if (currentOptionsString !== lastOptionsString) {
          lastOptionsString = currentOptionsString;
          const filteredOptions = this.filterOptions([...currentOptions]);

          if (JSON.stringify(filteredOptions) !== JSON.stringify(this.items)) {
            this.items = filteredOptions;
            this.cdr.detectChanges();
          }
        }
      };

      // Use requestAnimationFrame for efficient checking
      const scheduleCheck = () => {
        requestAnimationFrame(() => {
          checkOptionsChange();
          if (!this.destroy$.closed) {
            setTimeout(scheduleCheck, 500); // Check every 500ms instead of 1000ms
          }
        });
      };

      scheduleCheck();
    }
  }

  private filterOptions(options: ServiceOption[]): ServiceOption[] {
    const excludeIds = (this.props as ExtendedFieldProps).excludeIds || [];
    if (excludeIds.length === 0) {
      return options;
    }

    return options.filter(option => !excludeIds.includes(option.value));
  }

  private setupSearch(): void {
    this.searchTerm$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(async (term: any) => this.handleSearch(term)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private handleSearch(searchTerm: string): void {
    try {
      const props = this.props as ExtendedFieldProps;
      if (props.onSearch && typeof props.onSearch === 'function') {
        props.onSearch(searchTerm);
      }
    } catch (error) {
      console.error('Error during search:', error);
    }
  }

  fetchMoreItems(): void {
    try {
      const props = this.props as ExtendedFieldProps;
      if (
        props.hasMoreData &&
        typeof props.hasMoreData === 'function' &&
        props.onLoadMore &&
        typeof props.onLoadMore === 'function'
      ) {
        const hasMoreData = props.hasMoreData();
        const isLoadingMore = this.isLoadingMore();
        const isLoading = this.isLoading();

        if (hasMoreData && !isLoadingMore && !isLoading) {
          props.onLoadMore();
        }
      }
    } catch (error) {
      console.error('Error loading more data:', error);
    }
  }

  onScrollToEnd(): void {
    this.fetchMoreItems();
  }

  constructor(private cdr: ChangeDetectorRef, private elementRef: ElementRef) {
    super();
  }

  isLoading(): boolean {
    try {
      const props = this.props as ExtendedFieldProps;
      if (props.isLoading && typeof props.isLoading === 'function') {
        return props.isLoading();
      }
    } catch (error) {
      console.error('Error checking loading state:', error);
    }
    return false;
  }

  isLoadingMore(): boolean {
    try {
      const props = this.props as ExtendedFieldProps;
      if (props.isLoadingMore && typeof props.isLoadingMore === 'function') {
        return props.isLoadingMore();
      }
    } catch (error) {
      console.error('Error checking loading more state:', error);
    }
    return false;
  }

  hasMoreData(): boolean {
    try {
      const props = this.props as ExtendedFieldProps;
      if (props.hasMoreData && typeof props.hasMoreData === 'function') {
        return props.hasMoreData();
      }
    } catch (error) {
      console.error('Error checking has more data:', error);
    }
    return false;
  }
}
